import React from 'react';
import _ from 'underscore';
import {
  DOCUMENT_SIZE_LIMIT,
  DOCUMENT_SIZE_LIMIT_MB,
} from 'utils/DocumentsAPIUtils';
import { showToast } from 'stores/ToastStore';
import { Translation } from 'react-i18next';
import { CSV_FILE_TYPES } from '../config/accepted-file-types';

export function getDocKey(key) {
  if (key.includes('custom')) {
    return 'custom';
  }

  return key;
}

export function isPDF(type) {
  return type === 'application/pdf';
}

export function isCSV(type) {
  const csvTypes = CSV_FILE_TYPES.split(',').map((type) => type.trim());
  return csvTypes.includes(type);
}

export function isImage(type) {
  return (
    ['image/png', 'image/jpg', 'image/jpeg', 'image/tif', 'image/tiff'].indexOf(
      type,
    ) >= 0
  );
}

export const getCurrentDeliveryOrder = (docs) => {
  const deliveryOrders = docs.filter(
    (doc) =>
      doc.key === 'delivery_order' && doc.hasOwnProperty('show') && doc.show,
  );
  if (!deliveryOrders || !deliveryOrders.length) {
    return null;
  }

  const noCreatedAt = new Date('1900-01-01'); // If it's an old doc with no created_at, make it sort to the top.
  const sortedDeliveryOrders = _.sortBy(
    deliveryOrders,
    (doc) => doc.created_at || noCreatedAt,
  );
  const deliveryOrder = sortedDeliveryOrders[sortedDeliveryOrders.length - 1];
  return deliveryOrder;
};

export const validateDocumentFilesSizes = (filesOrDocuments) => {
  const isAnyDocumentMoreThenSizeLimit = Array.isArray(filesOrDocuments)
    ? filesOrDocuments.some(({ size }) => size > DOCUMENT_SIZE_LIMIT)
    : false;

  if (isAnyDocumentMoreThenSizeLimit) {
    const limitError = new Error(
      `Upload failed. File size must not exceed ${DOCUMENT_SIZE_LIMIT_MB} MBs.`,
    );
    const customLimitError = Object.assign(limitError, {
      _limit: DOCUMENT_SIZE_LIMIT_MB,
    });

    showToast(
      <Translation>{(t) => t('DOCUMENT_UPLOAD_FAILED_RETRY')}</Translation>,
      <Translation>
        {(t) => t('FILE_SIZE_TOO_BIG', { MB: DOCUMENT_SIZE_LIMIT_MB })}
      </Translation>,
      'error',
    );

    throw customLimitError;
  }
};

export const isPDFEncrypted = async (file) => {
  if (file && file.type === 'application/pdf') {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);

    return new Promise((resolve, reject) => {
      reader.onload = () => {
        const fileBlob = new Blob([reader.result], { type: 'application/pdf' });

        fileBlob.text().then((text) => {
          resolve(text.includes('/Encrypt'));
        });
      };

      reader.onerror = () => {
        reject(new Error('PDF_LOAD_ERROR'));
      };
    });
  }

  return false;
};

export const validatePDFEncryption = async (files) => {
  if (Array.isArray(files) && !files.length) {
    return;
  }

  if (!Array.isArray(files)) {
    files = [files];
  }

  const encryptionStates = await Promise.all(files.map(isPDFEncrypted));

  const isAnyPDFEncrypted = encryptionStates.some(
    (encryptionState) => encryptionState,
  );

  if (isAnyPDFEncrypted) {
    showToast(
      <Translation>{(t) => t('PDF_DOCUMENT_ENCRYPTED')}</Translation>,
      null,
      'error',
    );

    throw new Error('PDF_DOCUMENT_ENCRYPTED');
  }
};
